import { getLocale, translate } from '../../i18n/index.js';
import { useEffect, useRef, useState } from 'react';
import type { MembershipTenant } from '@atomfit/shared';
import { Navigate, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { getStripeClient } from '../../components/payment/stripe-client.js';
import axios from 'axios';
import { BackLink, formatCurrency, Spinner, Tag } from '../../components/design/AtomFitUI.js';
import { StripePaymentForm } from '../../components/payment/StripePaymentForm.js';
import { membershipsApi } from '../../api/memberships.api.js';
import { useStripeAppearance } from '../../hooks/useStripeAppearance.js';
import { useAuthStore } from '../../store/auth.store.js';
import { useMembershipCheckoutStore } from '../../store/membership-checkout.store.js';
import { resolveMembershipTenant } from '../../utils/membership-tenants.js';

export function MembershipCheckout() {
  const navigate = useNavigate();
  const stripeAppearance = useStripeAppearance();
  const account = useAuthStore((state) => state.account);
  const {
    selection,
    stripeAccountId,
    clientSecret,
    customerSessionClientSecret,
    subscriptionId,
    purchaseId,
    setPayment,
    resetPayment,
  } = useMembershipCheckoutStore();
  const stripePromise = getStripeClient(stripeAccountId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stripeLoadError, setStripeLoadError] = useState<string | null>(null);
  const [stripeElementsAttempt, setStripeElementsAttempt] = useState(0);
  const [alreadyPrepared, setAlreadyPrepared] = useState(false);
  const [tenant, setTenant] = useState<MembershipTenant | null>(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantError, setTenantError] = useState(false);
  const [tenantAttempt, setTenantAttempt] = useState(0);
  const preparingRef = useRef(false);
  const selectionTenantId = selection?.tenantId;
  const selectionGymId = selection?.gymId;

  useEffect(() => {
    let cancelled = false;
    setTenantLoading(true);
    setTenantError(false);
    setTenant(null);
    membershipsApi
      .tenants()
      .then((response) => {
        if (cancelled) return;
        // Legacy selections carry a gym ID. Resolve its current owner after a move.
        const current = selectionTenantId
          ? resolveMembershipTenant(response.data.data, {
              tenantId: selectionTenantId,
              ...(selectionGymId ? { gymId: selectionGymId } : {}),
            })
          : null;
        setTenant(current ?? null);
        if (current && current.id !== selectionTenantId) resetPayment();
      })
      .catch(() => {
        if (!cancelled) setTenantError(true);
      })
      .finally(() => {
        if (!cancelled) setTenantLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectionTenantId, selectionGymId, tenantAttempt, resetPayment]);
  const confirmPath =
    selection?.returnTo === 'checkout'
      ? '/membership-confirmed?returnTo=checkout'
      : '/membership-confirmed';
  const purchaseConfirmPath = `${confirmPath}${confirmPath.includes('?') ? '&' : '?'}purchaseId=${encodeURIComponent(purchaseId ?? '')}`;
  const backPath =
    selection?.returnTo === 'checkout'
      ? '/checkout'
      : selection?.returnTo === 'membership'
        ? '/gyms?flow=membership'
        : `/gyms/${selection?.gymId ?? ''}`;
  const backLabel =
    selection?.returnTo === 'checkout'
      ? translate('pages.public.membership_checkout.membership_checkout.back_label')
      : selection?.returnTo === 'membership'
        ? translate('pages.public.membership_checkout.membership_checkout.back_label_2')
        : translate('pages.public.membership_checkout.membership_checkout.back_label_3');

  async function requestMembershipPayment(force = false) {
    if (
      !selection ||
      !tenant ||
      tenantLoading ||
      tenantError ||
      (!force && (alreadyPrepared || clientSecret || error)) ||
      preparingRef.current
    ) {
      return;
    }

    preparingRef.current = true;
    setLoading(true);
    setError(null);
    setStripeLoadError(null);

    try {
      const response = await membershipsApi.purchase(selection.plan.id, tenant.id, true);
      setPayment({
        purchaseId: response.data.purchaseId,
        stripeAccountId: response.data.stripeAccountId,
        clientSecret: response.data.clientSecret,
        customerSessionClientSecret: response.data.customerSessionClientSecret,
        subscriptionId: response.data.subscriptionId ?? null,
      });
      setAlreadyPrepared(true);
      if (force) setStripeElementsAttempt((attempt) => attempt + 1);
      if (!response.data.clientSecret) {
        void navigate(
          `${confirmPath}${confirmPath.includes('?') ? '&' : '?'}purchaseId=${encodeURIComponent(response.data.purchaseId)}`,
        );
      }
    } catch (err) {
      if (isPasswordRequiredError(err)) {
        void navigate('/auth/create-password?redirect=%2Fmembership-checkout', { replace: true });
        return;
      }
      setError(getMembershipPurchaseErrorMessage(err));
    } finally {
      preparingRef.current = false;
      setLoading(false);
    }
  }

  function prepareMembershipPayment() {
    void requestMembershipPayment();
  }

  function reloadMembershipPayment() {
    void requestMembershipPayment(true);
  }

  if (!selection) {
    return <Navigate to="/membership" replace />;
  }

  if (tenantLoading)
    return (
      <main className="af-screen">
        <section className="af-shell">
          <Spinner />
        </section>
      </main>
    );
  if (tenantError)
    return (
      <main className="af-screen">
        <section className="af-shell">
          <BackLink to={backPath}>{backLabel}</BackLink>
          <div className="af-error">
            {translate('pages.public.gym_finder.gym_finder.div_error.error_gym')}
          </div>
          <button className="af-outline-btn" onClick={() => setTenantAttempt((value) => value + 1)}>
            {translate(
              'components.landing.pricing_section.membership_pricing.button_outline_btn.retry',
            )}
          </button>
        </section>
      </main>
    );
  if (!tenant)
    return (
      <Navigate
        to={selection.returnTo === 'checkout' ? '/checkout' : '/gyms?flow=membership'}
        replace
      />
    );

  const { plan } = selection;

  function retry() {
    preparingRef.current = false;
    resetPayment();
    setAlreadyPrepared(false);
    setError(null);
  }

  function cancelMembershipPayment() {
    void navigate(backPath);
  }

  return (
    <main className="af-screen af-checkout-screen">
      <section className="af-shell af-booking-flow-shell af-checkout-shell">
        <BackLink to={backPath}>{backLabel}</BackLink>
        <h1 style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-1px', margin: '0 0 6px' }}>
          {translate('pages.public.membership_checkout.membership_checkout.h1.payment_membership')}
        </h1>
        <p style={{ color: 'var(--af-muted)', margin: '0 0 28px', fontSize: 15 }}>
          {translate(
            'pages.public.membership_checkout.membership_checkout.p.membership_gym_tenant',
          )}
        </p>

        <div className="af-card af-checkout-summary">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              alignItems: 'flex-start',
            }}
          >
            <div>
              <div
                style={{
                  color: 'var(--af-muted)',
                  fontSize: 13,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                }}
              >
                {translate('pages.public.membership_checkout.membership_checkout.div.copy')}
              </div>
              <h2 style={{ margin: '6px 0 4px', fontSize: 24 }}>{plan.name}</h2>
              <div style={{ color: 'var(--af-muted)', fontSize: 13 }}>{tenant.name}</div>
              <ul style={{ color: 'var(--af-muted)', fontSize: 13, paddingLeft: 18 }}>
                {tenant.gyms.map((gym) => (
                  <li key={gym.id}>
                    {gym.name} · {gym.addressLine1}, {gym.city}
                  </li>
                ))}
              </ul>
            </div>
            <Tag>{plan.tier}</Tag>
          </div>

          <div style={{ display: 'grid', gap: 10, marginTop: 18 }}>
            <SummaryRow
              label={translate(
                'pages.public.membership_checkout.membership_checkout.summary_row.price',
              )}
              value={formatCurrency(plan.priceInSmallestUnit, plan.currency)}
            />

            <SummaryRow
              label={translate(
                'pages.public.membership_checkout.membership_checkout.summary_row.credit_edit',
              )}
              value={formatCurrency(plan.creditAmountInSmallestUnit, plan.currency)}
            />

            <SummaryRow
              label={translate(
                'pages.public.membership_checkout.membership_checkout.summary_row.slot',
              )}
              value={
                plan.slotPriceInSmallestUnit !== null
                  ? formatCurrency(plan.slotPriceInSmallestUnit, plan.currency)
                  : `-${plan.discountPercent}%`
              }
            />
          </div>
        </div>

        {!loading && !error && !clientSecret && !alreadyPrepared && (
          <div className="af-checkout-submit-row">
            <button
              className="af-outline-btn af-checkout-cancel"
              type="button"
              onClick={cancelMembershipPayment}
              style={{ padding: 16, fontSize: 17 }}
            >
              {translate(
                'pages.public.membership_checkout.membership_checkout.button_outline_btn_checkout_cancel.cancel',
              )}
            </button>
            <button
              className="af-primary-btn af-checkout-submit"
              type="button"
              onClick={prepareMembershipPayment}
              style={{ padding: 16, fontSize: 17 }}
            >
              {translate(
                'pages.public.membership_checkout.membership_checkout.button_primary_btn_checkout_submit.confirmation',
              )}
            </button>
          </div>
        )}

        {loading && (
          <div className="af-card af-checkout-payment-card">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                color: 'var(--af-muted)',
                fontWeight: 700,
              }}
            >
              <Spinner />
              {translate('pages.public.membership_checkout.membership_checkout.div.payment')}
            </div>
          </div>
        )}

        {!loading && error && (
          <>
            <div className="af-card af-checkout-payment-card">
              <div className="af-error">{error}</div>
            </div>
            <div className="af-checkout-submit-row">
              <button
                className="af-outline-btn af-checkout-cancel"
                type="button"
                onClick={cancelMembershipPayment}
                style={{ padding: 16 }}
              >
                {translate(
                  'pages.public.membership_checkout.membership_checkout.button_outline_btn_checkout_cancel.cancel_2',
                )}
              </button>
              <button
                className="af-primary-btn af-checkout-submit"
                type="button"
                onClick={retry}
                style={{ padding: 16 }}
              >
                {translate(
                  'pages.public.membership_checkout.membership_checkout.button_primary_btn_checkout_submit.retry',
                )}
              </button>
            </div>
          </>
        )}

        {!loading && !error && !clientSecret && alreadyPrepared && (
          <div className="af-card af-checkout-payment-card">
            <div>
              <p style={{ margin: '0 0 16px', color: 'var(--af-muted)', lineHeight: 1.5 }}>
                {translate('pages.public.membership_checkout.membership_checkout.p.membership')}
              </p>
              <button
                className="af-primary-btn"
                type="button"
                onClick={() => {
                  void navigate(purchaseConfirmPath);
                }}
                style={{ width: '100%', padding: 16 }}
              >
                {translate(
                  'pages.public.membership_checkout.membership_checkout.button_primary_btn.label',
                )}
              </button>
            </div>
          </div>
        )}

        {!loading && !error && clientSecret && !stripePromise && (
          <div className="af-card af-checkout-payment-card">
            <div className="af-error">
              {translate(
                'pages.public.membership_checkout.membership_checkout.div_error.payment_stripe',
              )}
            </div>
          </div>
        )}

        {!loading && !error && clientSecret && stripePromise && stripeLoadError && (
          <div className="af-card af-checkout-payment-card">
            <div className="af-error" style={{ marginBottom: 14 }}>
              {translate(
                'pages.public.membership_checkout.membership_checkout.div_error.error_payment',
              )}
              <div style={{ marginTop: 6, fontSize: 12 }}>{stripeLoadError}</div>
            </div>
            <button
              className="af-primary-btn"
              type="button"
              onClick={reloadMembershipPayment}
              style={{ width: '100%', padding: 16 }}
            >
              {translate(
                'pages.public.membership_checkout.membership_checkout.button_primary_btn.payment',
              )}
            </button>
          </div>
        )}

        {!loading && !error && clientSecret && stripePromise && !stripeLoadError && (
          <Elements
            key={`${stripeAccountId ?? 'platform'}:${stripeAppearance.key}:${stripeElementsAttempt}`}
            stripe={stripePromise}
            options={{
              clientSecret,
              locale: getLocale(),
              ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}),
              appearance: stripeAppearance.appearance,
            }}
          >
            <StripePaymentForm
              onLoadError={(message) => {
                setStripeLoadError(message);
              }}
              onSuccess={async () => {
                if (subscriptionId) {
                  try {
                    await membershipsApi.confirm(subscriptionId);
                  } catch (err) {
                    throw new Error(getMembershipConfirmErrorMessage(err));
                  }
                }
                void navigate(purchaseConfirmPath);
              }}
              returnUrl={`${window.location.origin}${purchaseConfirmPath}`}
              customerEmail={account?.email}
              savePaymentMethod
              splitSubmit
            />

            <div className="af-checkout-submit-row" style={{ marginTop: 0 }}>
              <button
                className="af-outline-btn af-checkout-cancel"
                type="button"
                onClick={cancelMembershipPayment}
                style={{ padding: 16, fontSize: 17 }}
              >
                {translate(
                  'pages.public.membership_checkout.membership_checkout.button_outline_btn_checkout_cancel.cancel_3',
                )}
              </button>
            </div>
          </Elements>
        )}
      </section>
    </main>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 14 }}>
      <span style={{ color: 'var(--af-muted)' }}>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function getMembershipConfirmErrorMessage(err: unknown) {
  if (!axios.isAxiosError(err)) {
    return err instanceof Error
      ? err.message
      : translate(
          'pages.public.membership_checkout.get_membership_confirm_error_message.error_payment_confirmation',
        );
  }

  const message = getApiErrorMessage(err.response?.data);
  switch (message) {
    case 'Membership subscription is not active':
      return translate(
        'pages.public.membership_checkout.get_membership_confirm_error_message.payment_membership_active',
      );
    case 'Membership not found':
      return translate(
        'pages.public.membership_checkout.get_membership_confirm_error_message.error_payment_membership',
      );
    default:
      return (
        message ||
        translate(
          'pages.public.membership_checkout.get_membership_confirm_error_message.error_payment_confirmation_2',
        )
      );
  }
}

function getMembershipPurchaseErrorMessage(err: unknown) {
  if (!axios.isAxiosError(err)) {
    return err instanceof Error
      ? err.message
      : translate(
          'pages.public.membership_checkout.get_membership_purchase_error_message.error_payment_membership',
        );
  }

  const message = getApiErrorMessage(err.response?.data);
  switch (message) {
    case 'Tenant has not connected a Stripe account':
      return translate(
        'pages.public.membership_checkout.get_membership_purchase_error_message.membership_tenant',
      );
    case 'Tenant Stripe account is not ready to accept payments':
      return translate(
        'pages.public.membership_checkout.get_membership_purchase_error_message.payment_tenant',
      );
    case 'Membership plan not found':
      return translate(
        'pages.public.membership_checkout.get_membership_purchase_error_message.membership',
      );
    case 'This customer account cannot buy memberships':
      return translate(
        'pages.public.membership_checkout.get_membership_purchase_error_message.membership_2',
      );
    default:
      return (
        message ||
        translate(
          'pages.public.membership_checkout.get_membership_purchase_error_message.error_payment_membership_2',
        )
      );
  }
}

function isPasswordRequiredError(err: unknown) {
  if (!axios.isAxiosError(err)) return false;
  const details = (err.response?.data as { error?: { details?: { reason?: unknown } } } | undefined)
    ?.error?.details;
  return details?.reason === 'PASSWORD_REQUIRED';
}

function getApiErrorMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const direct = (data as { message?: unknown }).message;
  if (typeof direct === 'string') return direct;
  const nested = (data as { error?: { message?: unknown } }).error?.message;
  return typeof nested === 'string' ? nested : null;
}
